import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import "./Users.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    role: "student",
    studentId: "",
    coachId: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, studentsRes, coachesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/students`),
        axios.get(`${API_URL}/api/coaches`),
      ]);
      setUsers(usersRes.data);
      setStudents(studentsRes.data);
      setCoaches(coachesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setFormData({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      role: "student",
      studentId: "",
      coachId: "",
    });
    setEditingUser(null);
    setShowCreateDialog(true);
  };

  const handleEditUser = (user) => {
    setFormData({
      email: user.email,
      password: "", // Don't populate password for editing
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      studentId: user.studentId || "",
      coachId: user.coachId || "",
    });
    setEditingUser(user);
    setShowCreateDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          studentId: formData.studentId || null,
          coachId: formData.coachId || null,
        };

        // Only include password if it's been changed
        if (formData.password) {
          updateData.password = formData.password;
        }

        await axios.put(`${API_URL}/api/users/${editingUser._id}`, updateData);
        toast.success("Benutzer aktualisiert");
      } else {
        // Create new user
        await axios.post(`${API_URL}/api/auth/register`, {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          studentId: formData.studentId || null,
          coachId: formData.coachId || null,
        });
        toast.success("Benutzer erstellt");
      }

      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(error.response?.data?.error || "Fehler beim Speichern");
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm("Benutzer wirklich deaktivieren?")) return;

    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
      toast.success("Benutzer deaktiviert");
      fetchData();
    } catch (error) {
      console.error("Error deactivating user:", error);
      toast.error("Fehler beim Deaktivieren");
    }
  };

  const handleActivate = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/users/${userId}/activate`);
      toast.success("Benutzer aktiviert");
      fetchData();
    } catch (error) {
      console.error("Error activating user:", error);
      toast.error("Fehler beim Aktivieren");
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin":
        return "role-badge role-admin";
      case "coach":
        return "role-badge role-coach";
      case "student":
        return "role-badge role-student";
      default:
        return "role-badge";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Admin";
      case "coach":
        return "Trainer";
      case "student":
        return "Schüler";
      default:
        return role;
    }
  };

  const getLinkedName = (user) => {
    if (user.role === "student" && user.studentId) {
      const student = students.find((s) => s._id === user.studentId);
      return student ? `${student.firstName} ${student.lastName}` : "Nicht gefunden";
    }
    if (user.role === "coach" && user.coachId) {
      const coach = coaches.find((c) => c._id === user.coachId);
      return coach ? `${coach.firstName} ${coach.lastName}` : "Nicht gefunden";
    }
    return "-";
  };

  if (loading) return <div className="loading">Laden...</div>;

  return (
    <div className="users-container">
      <div className="users-header">
        <h1>Benutzerverwaltung</h1>
        <button className="btn-create" onClick={handleCreateUser}>
          + Benutzer erstellen
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Verknüpft mit</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className={!user.isActive ? "user-inactive" : ""}>
                <td>
                  {user.firstName} {user.lastName}
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={getRoleBadgeClass(user.role)}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td>{getLinkedName(user)}</td>
                <td>
                  <span className={user.isActive ? "status-active" : "status-inactive"}>
                    {user.isActive ? "Aktiv" : "Inaktiv"}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-edit-small"
                    onClick={() => handleEditUser(user)}
                    disabled={!user.isActive}
                  >
                    Bearbeiten
                  </button>
                  {user.isActive ? (
                    <button
                      className="btn-deactivate-small"
                      onClick={() => handleDeactivate(user._id)}
                    >
                      Deaktivieren
                    </button>
                  ) : (
                    <button
                      className="btn-activate-small"
                      onClick={() => handleActivate(user._id)}
                    >
                      Aktivieren
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateDialog && (
        <div className="modal-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="user-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? "Benutzer bearbeiten" : "Neuer Benutzer"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>E-Mail *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>

              <div className="form-group">
                <label>Passwort {!editingUser && "*"}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? "Leer lassen, um nicht zu ändern" : ""}
                  minLength={8}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vorname *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Nachname *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Rolle *</label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value,
                      studentId: "",
                      coachId: "",
                    })
                  }
                  required
                >
                  <option value="student">Schüler</option>
                  <option value="coach">Trainer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role === "student" && (
                <div className="form-group">
                  <label>Schüler verknüpfen *</label>
                  <select
                    value={formData.studentId}
                    onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                    required
                  >
                    <option value="">-- Schüler auswählen --</option>
                    {students.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.firstName} {s.lastName} ({s.email || "keine E-Mail"})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.role === "coach" && (
                <div className="form-group">
                  <label>Trainer verknüpfen *</label>
                  <select
                    value={formData.coachId}
                    onChange={(e) => setFormData({ ...formData, coachId: e.target.value })}
                    required
                  >
                    <option value="">-- Trainer auswählen --</option>
                    {coaches.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.firstName} {c.lastName} ({c.email || "keine E-Mail"})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="dialog-buttons">
                <button type="submit" className="btn-save">
                  {editingUser ? "Aktualisieren" : "Erstellen"}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowCreateDialog(false)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
